import styled from '@emotion/styled';
import { useFormContext } from 'react-hook-form';

import { breakpoints, screen } from '@/components/common/breakpoints';
import { PrimaryButton } from '@/components/common/Buttons';
import { visuallyHiddenStyle } from '@/components/common/utils';
import {
  getLegalConsentFromMetadata,
  HubspotConsent,
} from '@/components/forms/HubspotFormNew';
import { HubspotFormField } from '@/components/forms/HubspotFormNew/HubspotFormField';
import { useMatchMedia } from '@/lib/useMatchMedia';
import { bodySmall } from '@/styles/typography';

export const HubspotFormEmailSignUp = ({ formData }: any) => {
  const {
    control,
    formState: { errors, isSubmitting },
  } = useFormContext();
  const variant = 'fresh';
  const { submitText, formFieldGroups, guid, metaData } = formData;
  const legalConsent = getLegalConsentFromMetadata(metaData);

  const isMobile = useMatchMedia(breakpoints.md);
  const isDesktop = !isMobile;
  if (formData?.formFieldGroups === undefined) return null;
  const [firstFieldGroup, ...rest] = formFieldGroups;
  return (
    <>
      <FormFieldsStack>
        <fieldset
          className={`form-columns-${firstFieldGroup.fields.length} first-field`}
          key="fieldGroup-First"
        >
          {firstFieldGroup.fields.map((field) => (
            <HubspotFormField
              formId={guid}
              key={field.name}
              field={field}
              control={control}
              error={errors[field.name]}
              variant={variant}
              colourTheme="dark"
            />
          ))}
        </fieldset>
        {isDesktop && (
          <DesktopSubmitButton
            componentTheme="dark"
            className={`hs-button hs-submit submit-btn footer-container ${
              isSubmitting ? 'disabled' : ''
            }`}
            type="submit"
          >
            {isSubmitting ? 'Submitting...' : submitText}
          </DesktopSubmitButton>
        )}
        {rest?.map((fieldGroup, index) => (
          <fieldset
            className={`form-columns-${fieldGroup.fields.length}`}
            // eslint-disable-next-line react/no-array-index-key
            key={`fieldGroup-${index}`}
          >
            {fieldGroup.fields.map((field) => (
              <HubspotFormField
                formId={guid}
                key={field.name}
                field={field}
                control={control}
                error={errors[field.name]}
                variant={variant}
                colourTheme="dark"
              />
            ))}
            {fieldGroup.richText?.type === 'TEXT' && (
              <div
                className="hs-richtext"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: fieldGroup.richText.content,
                }}
              />
            )}
          </fieldset>
        ))}
      </FormFieldsStack>
      {isMobile && (
        <MobileSubmitButton
          componentTheme="dark"
          className={`hs-button hs-submit submit-btn footer-container ${
            isSubmitting ? 'disabled' : ''
          }`}
          type="submit"
        >
          {isSubmitting ? 'Submitting...' : submitText}
        </MobileSubmitButton>
      )}
      {legalConsent && (
        <HubspotConsent theme="light" consentData={legalConsent} />
      )}
    </>
  );
};

const FormFieldsStack = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-x-small);
  align-items: center;

  .hs-richtext p {
    ${bodySmall}
    margin-top: var(--spacing-xxx-small);
  }

  ${screen.md} {
    grid-template-columns: 1fr auto;
    grid-template-rows: auto;
    > * {
      grid-column: span 2;
      &:nth-child(1) {
        grid-column: 1 / span 1;
        grid-row: 1 / 1;
      }
      &:nth-child(2) {
        grid-column: 2 / span 1;
        grid-row: 1 / 1;
      }
    }

    .first-field label {
      ${visuallyHiddenStyle}
    }
  }
`;

const DesktopSubmitButton = styled(PrimaryButton)`
  display: none;
  align-self: end;
  height: fit-content;
  font-weight: var(--font-weight-bold);

  &:hover {
    border: 1px solid var(--colour-white-dwarf-700);
  }

  ${screen.md} {
    display: flex;
  }
`;

const MobileSubmitButton = styled(PrimaryButton)`
  margin-top: var(--spacing-x-small);
  align-self: flex-end;
  justify-content: center;
  font-weight: var(--font-weight-bold);

  &:hover {
    border: 1px solid var(--colour-white-dwarf-700);
  }

  ${screen.md} {
    display: none;
  }
`;
